import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';

/**
 * ServiceHighlights component displays a set of problem-recognition services.
 * It includes hover animations and graceful error handling for unexpected
 * state values.
 */
const ServiceHighlights = () => {
  // State holds the index of the card currently being hovered.
  // Using a plain `null` initial value keeps the file JavaScript‑only
  // and avoids TypeScript‑only syntax that can cause parsing errors.
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Guard against non‑numeric values – this makes the component robust
  // if the state is ever set incorrectly (e.g., from external code).
  const isValidIndex = (idx) => Number.isInteger(idx) && idx >= 0;

  const services = [
    {
      icon: 'ri-search-eye-line',
      title: 'Problem Over-Identification',
      description:
        'Our specialists don\'t just find your problems — they find problems within your problems, then document sub-problems of those. Thoroughness is our minimum standard.',
      link: '/financial-success',
      color: 'from-amber-500/20 to-orange-600/20',
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Strategic Over-Complication',
      description:
        'Why have a simple problem when it can be a complex, multi-layered crisis? We apply our proprietary 14-step framework to ensure no issue remains straightforward.',
      link: '/financial-success',
      color: 'from-emerald-500/20 to-teal-600/20',
    },
    {
      icon: 'ri-brain-line',
      title: 'Premium Over-Thinking',
      description:
        'Our over-educated team will analyze your situation from every conceivable angle — and several inconceivable ones — until the original problem is barely recognizable.',
      link: '/financial-success',
      color: 'from-rose-500/20 to-pink-600/20',
    },
    {
      icon: 'ri-add-circle-line',
      title: 'Complimentary Bonus Problems',
      description:
        'As a value-added service, our process organically generates new problems at no extra charge. Think of it as compound interest, but for anxiety.',
      link: '/financial-success',
      color: 'from-violet-500/20 to-indigo-600/20',
    },
  ];

  return (
    <section className="bg-charcoal py-36 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
            Our Core Services
          </span>
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            We Don't Just Find Problems. We Multiply Them.
          </h2>
          <p className="text-xl text-light-gray max-w-2xl mx-auto">
            Industry-leading over-complication solutions delivered by a team of professionals who are, frankly, too educated for their own good
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="group relative h-full"
            >
              <Link to={service.link} className="block h-full">
                <div
                  className={`
                    relative bg-dark-gray rounded-2xl p-8 h-full border border-transparent
                    hover:border-gold/30 transition-all duration-500 overflow-hidden flex flex-col
                    ${
                      isValidIndex(hoveredIndex) && hoveredIndex === index
                        ? 'transform -translate-y-2'
                        : ''
                    }
                  `}
                >
                  {/* Gradient Background on Hover */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity:
                        isValidIndex(hoveredIndex) && hoveredIndex === index
                          ? 1
                          : 0,
                    }}
                    className={`absolute inset-0 bg-gradient-to-br ${service.color}`}
                  />

                  <div className="relative z-10 flex flex-col h-full">
                    <motion.div
                      animate={{
                        rotate:
                          isValidIndex(hoveredIndex) && hoveredIndex === index
                            ? 360
                            : 0,
                      }}
                      transition={{ duration: 0.6 }}
                      className="w-16 h-16 flex items-center justify-center rounded-xl bg-black/50 mb-6"
                    >
                      <i
                        className={`${service.icon} text-3xl text-gold`}
                        aria-hidden="true"
                      ></i>
                    </motion.div>

                    <h3 className="text-2xl font-bold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-light-gray text-base leading-relaxed flex-grow">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceHighlights;